import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, CircularProgress, MenuItem, Chip, Switch, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { getClassOffers, saveClassOffer, deleteClassOffer } from '../../../store/actions/classOfferActions';
import { getEstruturaLevelDisciplinas } from '../../../store/actions/estruturaDiscActions';
import { RootState } from '../../../store/reducers';
import { createTablePaginationWithFirstLast } from '../../../components/TablePaginationWithFirstLast';

const shiftOptions = [
    { value: 'Manhã', label: 'Manhã' },
    { value: 'Tarde', label: 'Tarde' },
    { value: 'Noite', label: 'Noite' },
];

const ClassOffers = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const classOffers = useSelector((state: RootState) => state.classOffers.classOffers);
    const isFetching = useSelector((state: RootState) => state.classOffers.isFetching);
    const levels = useSelector((state: RootState) => state.cadastros.estruturaDisciplinas);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState<any>(null);
    const [formData, setFormData] = useState<any>({
        Description: '', Level_id: '', Room: '', Shift: '',
        InitialDate: '', EndDate: '', isEnrollmentClosed: false
    });
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    useEffect(() => {
        dispatch(getClassOffers());
        dispatch(getEstruturaLevelDisciplinas());
    }, [dispatch]);

    const handleOpen = (item?: any) => {
        if (item) {
            setEditItem(item);
            setFormData({
                Description: item.Description || '',
                Level_id: item.Level_id || '',
                Room: item.Room || '',
                Shift: item.Shift || '',
                InitialDate: item.InitialDate ? item.InitialDate.substring(0, 10) : '',
                EndDate: item.EndDate ? item.EndDate.substring(0, 10) : '',
                isEnrollmentClosed: item.isEnrollmentClosed || false
            });
        } else {
            setEditItem(null);
            setFormData({
                Description: '', Level_id: '', Room: '', Shift: '',
                InitialDate: '', EndDate: '', isEnrollmentClosed: false
            });
        }
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditItem(null);
        setFormData({
            Description: '', Level_id: '', Room: '', Shift: '',
            InitialDate: '', EndDate: '', isEnrollmentClosed: false
        });
    };

    const handleSave = () => {
        const payload = editItem ? { ...formData, id: editItem.id } : { ...formData };
        dispatch(saveClassOffer(payload));
        handleClose();
        enqueueSnackbar('Salvo com sucesso!', { variant: 'success' });
    };

    const handleDelete = (id: number) => {
        dispatch(deleteClassOffer(id));
        setDeleteConfirm(null);
        enqueueSnackbar('Excluido com sucesso!', { variant: 'success' });
    };

    const handleChange = (field: string, value: any) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('pt-BR');
        } catch {
            return dateStr;
        }
    };

    const columns = [
        {
            name: 'id',
            label: 'ID',
            options: {
                display: false,
                filter: false,
                sort: true,
            }
        },
        {
            name: 'Description',
            label: 'Descrição',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'level',
            label: 'Nível',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: any) => value?.Description || '',
            }
        },
        {
            name: 'Room',
            label: 'Sala',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'Shift',
            label: 'Turno',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'InitialDate',
            label: 'Data Início',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => formatDate(value),
            }
        },
        {
            name: 'EndDate',
            label: 'Data Fim',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => formatDate(value),
            }
        },
        {
            name: 'isEnrollmentClosed',
            label: 'Matrícula',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: boolean) => (
                    <Chip
                        label={value ? 'Fechada' : 'Aberta'}
                        color={value ? 'error' : 'success'}
                        size="small"
                    />
                ),
            }
        },
        {
            name: 'actions',
            label: 'Ações',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const item = classOffers[dataIndex];
                    return (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(item.id);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    );
                }
            }
        }
    ];

    const options = {
        filterType: 'checkbox' as const,
        selectableRows: 'none' as const,
        responsive: 'standard' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50],
        customFooter: createTablePaginationWithFirstLast([15, 30, 50]),
        textLabels: {
            body: { noMatch: 'Nenhum registro encontrado', toolTip: 'Ordenar' },
            pagination: { next: 'Próxima', previous: 'Anterior', rowsPerPage: 'Linhas por página:', displayRows: 'de' },
            toolbar: { search: 'Buscar', downloadCsv: 'Download CSV', print: 'Imprimir', viewColumns: 'Colunas', filterTable: 'Filtrar' },
            filter: { all: 'Todos', title: 'Filtros', reset: 'Limpar' },
        },
        onRowClick: (_rowData: any, rowMeta: any) => {
            const item = classOffers[rowMeta.dataIndex];
            handleOpen(item);
        },
    };

    if (isFetching) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Turmas</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
                    Nova Turma
                </Button>
            </Box>

            <Paper>
                <MUIDataTable
                    title=""
                    data={classOffers || []}
                    columns={columns}
                    options={options}
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editItem ? 'Editar Turma' : 'Nova Turma'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Descrição"
                            value={formData.Description}
                            onChange={(e) => handleChange('Description', e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Nível"
                            select
                            value={formData.Level_id}
                            onChange={(e) => handleChange('Level_id', e.target.value)}
                            fullWidth
                            required
                        >
                            {(levels || []).map((level: any) => (
                                <MenuItem key={level.id} value={level.id}>
                                    {level.Description}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Sala"
                            value={formData.Room}
                            onChange={(e) => handleChange('Room', e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Turno"
                            select
                            value={formData.Shift}
                            onChange={(e) => handleChange('Shift', e.target.value)}
                            fullWidth
                        >
                            {shiftOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Data Início"
                            type="date"
                            value={formData.InitialDate}
                            onChange={(e) => handleChange('InitialDate', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Data Fim"
                            type="date"
                            value={formData.EndDate}
                            onChange={(e) => handleChange('EndDate', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isEnrollmentClosed}
                                    onChange={(e) => handleChange('isEnrollmentClosed', e.target.checked)}
                                />
                            }
                            label="Matrícula Fechada"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!formData.Description}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir esta turma?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                    <Button
                        onClick={() => deleteConfirm !== null && handleDelete(deleteConfirm)}
                        color="error"
                        variant="contained"
                    >
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClassOffers;
